/**
 * Pure URL construction for the cross-user noVNC tunnel WebSocket (P4.D.X —
 * the LexPortal operator view of another user's live Chromium).
 *
 * Mirrors lexide's `src/components/agent-browser/vnc-url.ts` (the proven
 * P4.D.W builder), with one difference baked into the CALLER, not here: the
 * tunnel path carries the **viewed user's** id (`/ws/runtime/<targetUserId>/…`),
 * not the operator's own. This module is path-agnostic — it just resolves a
 * relative tunnel path against the lexserver WS origin + normalises the scheme.
 *
 * Why it must NOT use `window.location.origin`: the LexPortal SPA host has no
 * `/ws/*` reverse proxy, so a WS built against the page origin dies at the SPA
 * App Service edge (WS close 1006). Every LexPortal WS must target the
 * lexserver host via `VITE_LEXSERVER_WS_URL` (the same lesson chat `/ws` +
 * lexide's VNC pane already encode). localdev masks it (Vite dev-proxy makes
 * same-origin correct). See ai-docs/lexruntime-noVNC-monkey-patch.md §10.
 */

/** The lexserver WebSocket origin (scheme + host) the runtime tunnel attaches
 *  to. Prod: origin of `VITE_LEXSERVER_WS_URL`. Dev (unset): same-origin → the
 *  Vite dev-proxy forwards `/ws/*` to the local lexserver. Malformed env →
 *  same-origin fallback rather than throw. */
export function resolveRuntimeWsOrigin(): string {
  const fromEnv = (import.meta.env.VITE_LEXSERVER_WS_URL as string | undefined)?.trim();
  if (fromEnv) {
    try {
      const u = new URL(fromEnv);
      return `${u.protocol}//${u.host}`;
    } catch {
      // Malformed env → fall through to same-origin rather than throw.
    }
  }
  return window.location.origin;
}

/** Resolve the (already `targetUserId`-prefixed) tunnel path against the
 *  lexserver WS origin, normalise the scheme to `ws`/`wss` (leaving an
 *  already-ws(s) origin untouched), and append the short-TTL `?token=`. Pure
 *  given `wsOrigin`. */
export function buildVncWsUrl(
  vncEndpoint: string,
  token: string,
  wsOrigin: string = resolveRuntimeWsOrigin(),
): string {
  const u = new URL(vncEndpoint, wsOrigin);
  if (u.protocol === "https:") u.protocol = "wss:";
  else if (u.protocol === "http:") u.protocol = "ws:";
  u.searchParams.set("token", token);
  return u.toString();
}
